/** @jsxImportSource @emotion/react */
import { css, jsx } from '@emotion/react';
import React from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { Header } from 'src/common/ui/components/Header';

/**
 * A flexible `Page` component that serves as a container for content.
 * It allows customization of the HTML element, styling, and layout properties.
 *
 * @param children - The content to be rendered inside the `Page` component.
 * @param className - Optional additional CSS class names to apply to the component.
 * @param component - The HTML element to render as the root of the `Page`. Defaults to `'main'`.
 * @param fullWidth - Whether the `Page` should span the full width of its container. Defaults to `false`.
 * @param padding - Whether to apply padding to the `Page`. Defaults to `true`.
 */
export const Page = ({
  children,
  className,
  component = 'main',
  fullWidth = false,
  padding = true,
}: React.PropsWithChildren<{
  component?: 'main' | 'section' | 'article' | 'div';
  className?: string;
  fullWidth?: boolean;
  padding?: boolean;
}>) =>
  jsx(
    component,
    {
      className,
      css: css`
        padding: ${padding ? Spacing.Unit4 : 0}px;
        max-width: ${fullWidth ? '100%' : '600px'};

        @media print {
          max-width: 100%;
        }
      `,
      'data-testid': 'nds-component-page',
    },
    children,
  );

export const PageTitle = ({
  children,
  className,
}: React.PropsWithChildren<{
  className?: string;
}>) => (
  <Header
    css={css`
      color: ${Colors.CoolGray6};
    `}
    className={className}
  >
    {children}
  </Header>
);
